import {
  ActivityLog,
  ApiImportData,
  ApiListParams,
  ApiListResponse,
  ApiResponse,
  PresignedUrl,
  PresignedUrlPayload,
  UploadProgress,
} from "../types";
import { AxiosProgressEvent } from "axios";
import { api } from "../libs";

export const importData: ({
  data,
  progressCallback,
}: {
  url: string;
  data: FormData;
  progressCallback: (progressEvent: AxiosProgressEvent) => void;
}) => Promise<ApiResponse<ApiImportData>> = async ({
  url,
  data,
  progressCallback,
}) => {
  try {
    const response = await api.post(url, data, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
      onUploadProgress: progressCallback,
    });
    return response.data;
  } catch (error) {
    console.error("Error importing data:", error);
    throw error;
  }
};

export const downloadData: ({
  url,
  params,
}: {
  url: string;
  params: any;
}) => Promise<{
  data: any;
  filename: string;
}> = async ({ url, params }) => {
  try {
    const response = await api.get(url, {
      params,
      responseType: "blob",
    });
    return {
      data: response.data,
      filename: response.headers["content-disposition"]
        ?.split("=")[1]
        ?.replace(/"/g, ""),
    };
  } catch (error) {
    console.error("Error downloading data:", error);
    throw error;
  }
};

export const requestPresignedUrls = async (
  filenames: string[]
): Promise<ApiResponse<PresignedUrl[]>> => {
  try {
    const response = await api.post("/uploads/presign", {
      filenames,
    });
    return response.data.data;
  } catch (error: any) {
    throw new Error(`Failed to request presigned URLs: ${error.message}`);
  }
};

/**
 * Upload a single file to presigned URL with progress tracking
 */
export const uploadFileToPresignedUrl = async (
  file: File,
  uploadUrl: string,
  onProgress?: (progress: UploadProgress) => void
): Promise<void> => {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    // Track upload progress
    xhr.upload.addEventListener("progress", (event) => {
      if (event.lengthComputable && onProgress) {
        const progress: UploadProgress = {
          filename: file.name,
          loaded: event.loaded,
          total: event.total,
          percentage: Math.round((event.loaded / event.total) * 100),
        };
        onProgress(progress);
      }
    });
    // Handle completion
    xhr.addEventListener("load", () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve();
      } else {
        reject(
          new Error(
            `AWS S3 upload failed with status: ${xhr.status} - ${xhr.statusText}`
          )
        );
      }
    });
    // Handle errors
    xhr.addEventListener("error", () => {
      reject(new Error("AWS S3 upload failed due to network error"));
    });
    // Handle abort
    xhr.addEventListener("abort", () => {
      reject(new Error("AWS S3 upload was cancelled"));
    });
    // Start upload - AWS S3 presigned URLs work with PUT method
    xhr.open("PUT", uploadUrl);
    // AWS S3 requires Content-Type header for proper file handling
    xhr.setRequestHeader(
      "Content-Type",
      file.type || "application/octet-stream"
    );
    // Send the file directly to AWS S3
    xhr.send(file);
  });
};

/**
 * Upload multiple files in parallel with concurrency control
 */
export const uploadFilesInParallel = async (
  files: { file: File; upload_url: string; id: string }[],
  onProgress?: (fileId: string, progress: UploadProgress) => void,
  maxConcurrent: number = 3
): Promise<void> => {
  const semaphore = new Semaphore(maxConcurrent);
  const uploadPromises = files.map(({ file, upload_url, id }) =>
    semaphore.acquire().then(async (release) => {
      try {
        await uploadFileToPresignedUrl(
          file,
          upload_url,
          onProgress ? (progress) => onProgress(id, progress) : undefined
        );
      } finally {
        release();
      }
    })
  );
  await Promise.all(uploadPromises);
};
/**
 * Simple semaphore for concurrency control
 */
class Semaphore {
  private permits: number;
  private queue: (() => void)[] = [];
  constructor(permits: number) {
    this.permits = permits;
  }
  async acquire(): Promise<() => void> {
    return new Promise((resolve) => {
      if (this.permits > 0) {
        this.permits--;
        resolve(() => this.release());
      } else {
        this.queue.push(() => {
          this.permits--;
          resolve(() => this.release());
        });
      }
    });
  }
  private release(): void {
    this.permits++;
    if (this.queue.length > 0) {
      const next = this.queue.shift()!;
      next();
    }
  }
}

export const getChangelogs: (
  id: string,
  params?: ApiListParams<any>
) => Promise<ApiListResponse<ActivityLog>> = async (url, params) => {
  try {
    const response = await api.get(url, {
      params: params ?{
        page: params.page,
        limit: params.limit,
        ...params.filters,
      } : {},
    });
    return response.data;
  } catch (error) {
    console.error("Error fetching change logs:", error);
    throw error;
  }
};
